import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

/** 서버 한 페이지 상한 — `limit` 1–50. */
export const MEDIA_PAGE_LIMIT = 50;
/** 서버 cursor 상한(문자 수). 넘으면 서버가 422로 거절한다. */
const MAX_CURSOR_LENGTH = 16_384;

export const MEDIA_TARGET_KINDS = [
  'PROPERTY',
  'ROOM_TYPE',
  'ROOM',
  'SLOT',
  'PROPERTY_FLOOR',
] as const;
export const MEDIA_ITEM_KINDS = ['IMAGE', 'VIDEO', 'VR', 'DOCUMENT'] as const;
export const PUBLICATION_STATUSES = ['PUBLISHED', 'UNPUBLISHED'] as const;
export const PUBLICATION_RECORD_STATES = ['LIVE', 'DELETED', 'ABSENT'] as const;
export const SELECTION_ROLES = [
  'PROPERTY_CARD',
  'PROPERTY_HERO',
  'ROOM_TYPE_GALLERY',
  'SLOT_FLOOR_PLAN',
  'UNIT_FLOOR_PLAN',
  'COMMON_AREA',
] as const;
export const OBSERVATION_STATES = ['CURRENT', 'ABSENT', 'QUARANTINED'] as const;
export const TYPE_RELATION_STATES = ['NOT_APPLICABLE', 'RESOLVED', 'UNRESOLVED'] as const;

export type MediaTargetKind = (typeof MEDIA_TARGET_KINDS)[number];
export type MediaItemKind = (typeof MEDIA_ITEM_KINDS)[number];
export type PublicationStatus = (typeof PUBLICATION_STATUSES)[number];
export type PublicationRecordState = (typeof PUBLICATION_RECORD_STATES)[number];
export type SelectionRole = (typeof SELECTION_ROLES)[number];
export type ObservationState = (typeof OBSERVATION_STATES)[number];
export type TypeRelationState = (typeof TYPE_RELATION_STATES)[number];
export type MediaDirection = 'asc' | 'desc';
export type MediaMarkerState = 'VALUE' | 'NULL' | 'MISSING';
export interface MediaMarker {
  state: MediaMarkerState;
  value: boolean | number | string | null;
}
export interface MediaObservation {
  recordKey: string;
  isActive: MediaMarker;
  isPublished: MediaMarker;
  deleted: MediaMarker;
}

/**
 * 조회할 한 공간. 서버는 선택자를 섞어 쓰는 것을 허용하지 않는다 —
 * PROPERTY는 id·floor 없음, ROOM_TYPE/ROOM/SLOT은 id만, PROPERTY_FLOOR는 정수 floor(0·음수 포함)만.
 */
export interface MediaTarget {
  kind: MediaTargetKind;
  id: string | null;
  floor: number | null;
}

export interface MediaPageRequest {
  direction: MediaDirection;
  cursor?: string | null;
  limit?: number;
}

export interface MediaScope {
  propertyId: string;
  targetKind: MediaTargetKind;
  targetId: string | null;
  floor: number | null;
  roomId: string | null;
  roomTypeId: string | null;
  typeRelationState: TypeRelationState;
}
export interface MediaPresentation {
  id: string;
  contentRevision: number;
}
/** 저장된 공개 선택 header. 공개 적격성은 서버도 계산하지 않는다(`NOT_EVALUATED`). */
export interface MediaPublication {
  id: string;
  status: PublicationStatus;
  revision: number;
  presentationRevision: number | null;
}
export interface MediaGroup {
  id: string;
  collectionRevision: number | null;
  presentationId: string | null;
  groupKey: string | null;
  purpose: string;
  language: string;
  /** null·빈 문자열을 구분해 보존한다. */
  categoryName: string | null;
  sourceOrder: number | null;
  componentKey: string | null;
  observationState: ObservationState | null;
}
export interface MediaSelection {
  id: string;
  publicationId: string;
  role: SelectionRole;
  groupOrder: number;
  imageOrder: number;
  category: string | null;
  language: string;
}
export interface MediaItem {
  id: string;
  groupId: string;
  kind: MediaItemKind;
  primaryUri: string | null;
  thumbnailUri: string | null;
  codecVersion?: 3;
  contentPath?: string;
  sourceOrder: number | null;
  /** 원문의 표시 위치 키. 판매·점유 slot과 무관하다. */
  contentSlot: string | null;
  observationState: ObservationState | null;
  savedPublicSelections: MediaSelection[];
}
export interface MediaPageInfo {
  limit: number;
  direction: MediaDirection;
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
}
interface MediaContext {
  scope: MediaScope;
  /** CORE 표현이 없는 native collection도 null을 반환한다. */
  presentation: MediaPresentation | null;
  publication: MediaPublication | null;
  publicationRecordState: PublicationRecordState;
  page: MediaPageInfo;
}
export interface MediaGroupsPage extends MediaContext {
  groups: MediaGroup[];
}
export interface MediaItemsPage extends MediaContext {
  group: MediaGroup;
  items: MediaItem[];
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const text = (value: unknown): value is string => typeof value === 'string';
const nullableText = (value: unknown): value is string | null => value === null || text(value);
const nullableInt = (value: unknown): value is number | null =>
  value === null || Number.isSafeInteger(value);
const nonNegative = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const positive = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 1;
const nullableUuid = (value: unknown): value is string | null => value === null || isUuid(value);
const oneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (list as readonly string[]).includes(value);
const sameId = (left: string | null, right: string | null) =>
  left === right || (left !== null && right !== null && left.toLowerCase() === right.toLowerCase());

function invalid() {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '사진·영상을 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
}

/** 서버가 받아 주는 선택자 조합인지 — 어기면 422이므로 호출 전에 거른다. */
export function isValidMediaTarget(target: MediaTarget): boolean {
  switch (target.kind) {
    case 'PROPERTY':
      return target.id === null && target.floor === null;
    case 'ROOM_TYPE':
    case 'ROOM':
    case 'SLOT':
      return isUuid(target.id) && target.floor === null;
    case 'PROPERTY_FLOOR':
      return target.id === null && Number.isSafeInteger(target.floor);
    default:
      return false;
  }
}

/** 서버 cursor는 base64url 불투명 문자열이다. 모양이 아니면 보내지 않는다. */
export function isMediaCursor(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= MAX_CURSOR_LENGTH &&
    /^[\w-]+$/.test(value)
  );
}

function checkPageRequest(request: MediaPageRequest) {
  const limit = request.limit ?? MEDIA_PAGE_LIMIT;
  const cursor = request.cursor ?? null;
  if (
    !(['asc', 'desc'] as readonly string[]).includes(request.direction) ||
    !Number.isInteger(limit) ||
    limit < 1 ||
    limit > MEDIA_PAGE_LIMIT ||
    !(cursor === null || isMediaCursor(cursor))
  )
    throw invalid();
  return { limit, cursor, direction: request.direction };
}

function parseGroup(value: unknown, native = false): MediaGroup {
  if (
    !record(value) ||
    !exactKeys(value, [
      'id',
      'presentation_id',
      'group_key',
      'purpose',
      'language',
      'category_name',
      'source_order',
      'component_key',
      'observation_state',
      ...('collection_revision' in value ? ['collection_revision'] : []),
    ]) ||
    !(
      value.collection_revision === undefined ||
      value.collection_revision === null ||
      positive(value.collection_revision)
    ) ||
    !isUuid(value.id) ||
    (native
      ? value.presentation_id !== null ||
        !positive(value.collection_revision) ||
        value.observation_state !== 'CURRENT'
      : !nullableUuid(value.presentation_id)) ||
    // Current content collections may have no source category key, even with a presentation.
    !nullableText(value.group_key) ||
    !text(value.purpose) ||
    !text(value.language) ||
    !nullableText(value.category_name) ||
    !nullableInt(value.source_order) ||
    !nullableText(value.component_key) ||
    !(value.observation_state === null || oneOf(OBSERVATION_STATES, value.observation_state))
  )
    throw invalid();
  return {
    id: value.id,
    collectionRevision: value.collection_revision ?? null,
    presentationId: value.presentation_id as string | null,
    groupKey: value.group_key,
    purpose: value.purpose,
    language: value.language,
    categoryName: value.category_name,
    sourceOrder: value.source_order,
    componentKey: value.component_key,
    observationState: value.observation_state,
  };
}

function parseSelection(value: unknown): MediaSelection {
  if (
    !record(value) ||
    !exactKeys(value, [
      'id',
      'publication_id',
      'role',
      'group_order',
      'image_order',
      'category',
      'language',
    ]) ||
    !isUuid(value.id) ||
    !isUuid(value.publication_id) ||
    !oneOf(SELECTION_ROLES, value.role) ||
    !nonNegative(value.group_order) ||
    !nonNegative(value.image_order) ||
    !nullableText(value.category) ||
    !text(value.language)
  )
    throw invalid();
  return {
    id: value.id,
    publicationId: value.publication_id,
    role: value.role,
    groupOrder: value.group_order,
    imageOrder: value.image_order,
    category: value.category,
    language: value.language,
  };
}

function parseItem(value: unknown): MediaItem {
  const managed = record(value) && value.codec_version === 3;
  if (
    !record(value) ||
    !exactKeys(value, [
      'id',
      'group_id',
      'kind',
      'primary_uri',
      'thumbnail_uri',
      'source_order',
      'content_slot',
      'observation_state',
      'saved_public_selections',
      ...(managed ? ['codec_version', 'content_path'] : []),
    ]) ||
    !isUuid(value.id) ||
    !isUuid(value.group_id) ||
    !oneOf(MEDIA_ITEM_KINDS, value.kind) ||
    (managed
      ? value.kind !== 'IMAGE' ||
        value.primary_uri !== null ||
        value.thumbnail_uri !== null ||
        value.observation_state !== 'CURRENT' ||
        value.content_path !== managedContentPath(value.group_id, value.id)
      : !text(value.primary_uri) || value.primary_uri === '') ||
    !nullableText(value.thumbnail_uri) ||
    !nullableInt(value.source_order) ||
    !nullableText(value.content_slot) ||
    !(value.observation_state === null || oneOf(OBSERVATION_STATES, value.observation_state)) ||
    !Array.isArray(value.saved_public_selections)
  )
    throw invalid();
  return {
    id: value.id,
    groupId: value.group_id,
    kind: value.kind,
    primaryUri: value.primary_uri as string | null,
    thumbnailUri: value.thumbnail_uri,
    ...(managed ? { codecVersion: 3 as const, contentPath: value.content_path as string } : {}),
    sourceOrder: value.source_order,
    contentSlot: value.content_slot,
    observationState: value.observation_state,
    savedPublicSelections: value.saved_public_selections.map(parseSelection),
  };
}

export function managedContentPath(groupId: unknown, itemId: unknown): string | null {
  return isUuid(groupId) && isUuid(itemId)
    ? `/api/v1/admin/media-collections/${groupId}/managed-items/${itemId}/content`
    : null;
}

/** The Core client owns the base URL and bearer token. Never fetch a server supplied URL. */
export async function readManagedImage(
  client: ServiceClient,
  item: MediaItem,
  signal?: AbortSignal,
): Promise<Blob> {
  const expected = managedContentPath(item.groupId, item.id);
  if (item.codecVersion !== 3 || !expected || item.contentPath !== expected) throw invalid();
  const blob = await client.blob(expected.slice('/api/v1/'.length), { signal });
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(blob.type) || blob.size > 10_000_000)
    throw invalid();
  return blob;
}

function parseScope(value: unknown, propertyId: string): MediaScope {
  if (
    !record(value) ||
    !isUuid(value.property_id) ||
    !sameId(value.property_id, propertyId) ||
    !oneOf(MEDIA_TARGET_KINDS, value.target_kind) ||
    !nullableUuid(value.target_id) ||
    !nullableInt(value.floor) ||
    !nullableUuid(value.room_id) ||
    !nullableUuid(value.room_type_id) ||
    !oneOf(TYPE_RELATION_STATES, value.type_relation_state)
  )
    throw invalid();
  return {
    propertyId: value.property_id,
    targetKind: value.target_kind,
    targetId: value.target_id,
    floor: value.floor,
    roomId: value.room_id,
    roomTypeId: value.room_type_id,
    typeRelationState: value.type_relation_state,
  };
}

/** Native responses have actual Core ancestry, never a fabricated presentation. */
function validNativeMediaScope(scope: MediaScope): boolean {
  if (scope.floor !== null) return false;
  switch (scope.targetKind) {
    case 'PROPERTY':
      return (
        scope.targetId === null &&
        scope.roomId === null &&
        scope.roomTypeId === null &&
        scope.typeRelationState === 'NOT_APPLICABLE'
      );
    case 'ROOM_TYPE':
      return (
        scope.roomId === null &&
        scope.roomTypeId !== null &&
        sameId(scope.targetId, scope.roomTypeId) &&
        scope.typeRelationState === 'RESOLVED'
      );
    case 'ROOM':
      return (
        scope.roomId !== null &&
        sameId(scope.targetId, scope.roomId) &&
        scope.roomTypeId !== null &&
        scope.typeRelationState === 'RESOLVED'
      );
    case 'SLOT':
      return (
        scope.targetId !== null &&
        scope.roomId !== null &&
        scope.roomTypeId !== null &&
        scope.typeRelationState === 'RESOLVED'
      );
    default:
      return false;
  }
}

function parseContext(
  value: Record<string, unknown>,
  propertyId: string,
  request: { limit: number; direction: MediaDirection },
  itemCount: number,
  native = false,
): MediaContext {
  const scope = parseScope(value.scope, propertyId);
  if (native && !validNativeMediaScope(scope)) throw invalid();
  let presentation: MediaPresentation | null = null;
  if (value.presentation !== null) {
    const entry = value.presentation;
    if (!record(entry) || !isUuid(entry.id) || !positive(entry.content_revision)) throw invalid();
    presentation = {
      id: entry.id,
      contentRevision: entry.content_revision,
    };
  }
  let publication: MediaPublication | null = null;
  if (value.publication !== null) {
    const entry = value.publication;
    if (
      !record(entry) ||
      !isUuid(entry.id) ||
      !oneOf(PUBLICATION_STATUSES, entry.status) ||
      !positive(entry.revision) ||
      // The compatibility projection has no legacy revision for current content assignments.
      (native
        ? entry.presentation_revision !== null
        : !(entry.presentation_revision === null || positive(entry.presentation_revision)))
    )
      throw invalid();
    publication = {
      id: entry.id,
      status: entry.status,
      revision: entry.revision,
      presentationRevision: entry.presentation_revision as number | null,
    };
  }
  if (
    !oneOf(PUBLICATION_RECORD_STATES, value.publication_record_state) ||
    // header가 없을 때만 ABSENT다. header는 표현이 있어야 읽는다.
    (publication === null) !== (value.publication_record_state === 'ABSENT') ||
    (!native && presentation === null && publication !== null) ||
    value.section_order !== null ||
    value.section_order_state !== 'UNKNOWN_NOT_STORED' ||
    value.public_eligibility !== 'NOT_EVALUATED'
  )
    throw invalid();
  const info = value.page;
  if (
    !record(info) ||
    info.limit !== request.limit ||
    info.direction !== request.direction ||
    !nonNegative(info.total) ||
    info.total < itemCount ||
    typeof info.has_more !== 'boolean' ||
    !nullableText(info.next_cursor) ||
    // 다음 페이지가 있으면 cursor가 있고, 없으면 없다. 표현이 없으면 페이지도 비어 있다.
    info.has_more !== (info.next_cursor !== null) ||
    (info.next_cursor !== null && !isMediaCursor(info.next_cursor)) ||
    info.completeness !== 'PAGE' ||
    info.snapshot_consistency !== 'REQUEST' ||
    (!native && presentation === null && (itemCount !== 0 || info.total !== 0))
  )
    throw invalid();
  return {
    scope,
    presentation,
    publication,
    publicationRecordState: value.publication_record_state,
    page: {
      limit: request.limit,
      direction: request.direction,
      total: info.total,
      hasMore: info.has_more,
      nextCursor: info.next_cursor,
    },
  };
}

/**
 * 한 공간의 CORE 미디어 그룹을 읽는다 — `GET admin/properties/{id}/media-groups`.
 * 그룹은 `source_order` NULLS LAST, 같으면 UUID 순이다. cursor는 대상·방향에 묶인 불투명 값이라
 * 대상이나 방향이 바뀌면 처음부터 다시 읽어야 한다.
 */
export async function readPropertyMediaGroups(
  client: ServiceClient,
  propertyId: string,
  target: MediaTarget,
  request: MediaPageRequest,
  signal?: AbortSignal,
  nativeCurrent = false,
): Promise<MediaGroupsPage> {
  const { limit, cursor, direction } = checkPageRequest(request);
  if (!isUuid(propertyId) || !isValidMediaTarget(target)) throw invalid();
  const value = await client.get<unknown>(`admin/properties/${propertyId}/media-groups`, {
    params: {
      target_kind: target.kind,
      target_id: target.id ?? undefined,
      floor: target.floor ?? undefined,
      limit,
      direction,
      cursor: cursor ?? undefined,
    },
    signal,
  });
  if (
    !record(value) ||
    !exactKeys(value, [
      'scope',
      'presentation',
      'publication',
      'publication_record_state',
      'section_order',
      'section_order_state',
      'public_eligibility',
      'items',
      'page',
    ]) ||
    !Array.isArray(value.items) ||
    value.items.length > limit
  )
    throw invalid();
  const native = nativeCurrent && value.presentation === null;
  const groups = value.items.map((item) => parseGroup(item, native));
  const context = parseContext(value, propertyId, { limit, direction }, groups.length, native);
  if (
    context.scope.targetKind !== target.kind ||
    !sameId(context.scope.targetId, target.id) ||
    context.scope.floor !== target.floor ||
    new Set(groups.map((group) => group.id)).size !== groups.length ||
    groups.some((group) => !sameId(group.presentationId, context.presentation?.id ?? null))
  )
    throw invalid();
  return { ...context, groups };
}

/**
 * 한 그룹의 항목과 항목별 저장된 공개 선택을 읽는다 — `GET admin/properties/{id}/media-groups/{group}/items`.
 * 대상은 서버가 그룹에서 정하므로 선택자를 보내지 않는다.
 */
export async function readPropertyMediaItems(
  client: ServiceClient,
  propertyId: string,
  groupId: string,
  request: MediaPageRequest,
  signal?: AbortSignal,
  nativeCurrent = false,
): Promise<MediaItemsPage> {
  const { limit, cursor, direction } = checkPageRequest(request);
  if (!isUuid(propertyId) || !isUuid(groupId)) throw invalid();
  const value = await client.get<unknown>(
    `admin/properties/${propertyId}/media-groups/${groupId}/items`,
    { params: { limit, direction, cursor: cursor ?? undefined }, signal },
  );
  if (
    !record(value) ||
    !exactKeys(value, [
      'scope',
      'presentation',
      'publication',
      'publication_record_state',
      'section_order',
      'section_order_state',
      'public_eligibility',
      'group',
      'items',
      'page',
    ]) ||
    !Array.isArray(value.items) ||
    value.items.length > limit
  )
    throw invalid();
  const items = value.items.map(parseItem);
  const native = nativeCurrent && value.presentation === null;
  const context = parseContext(value, propertyId, { limit, direction }, items.length, native);
  const group = parseGroup(value.group, native);
  const selectionCount = items.reduce((sum, item) => sum + item.savedPublicSelections.length, 0);
  if (
    // 항목을 읽을 수 있다는 것은 그룹이 속한 CORE 표현이 있다는 뜻이다.
    (!native && context.presentation === null) ||
    !sameId(group.id, groupId) ||
    // A native collection can be read beside a same-scope compatibility presentation.
    (group.presentationId === null
      ? !validNativeMediaScope(context.scope)
      : !sameId(group.presentationId, context.presentation?.id ?? null)) ||
    new Set(items.map((item) => item.id)).size !== items.length ||
    items.some((item) => !sameId(item.groupId, group.id)) ||
    (selectionCount > 0 && context.publication === null) ||
    items.some((item) =>
      item.savedPublicSelections.some(
        (selection) => !sameId(selection.publicationId, context.publication?.id ?? null),
      ),
    )
  )
    throw invalid();
  return { ...context, group, items };
}
